import { useField } from 'formik';
import _ from 'lodash';
import { rgba } from 'polished';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { AddIcon, HammerIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { IconColor, Paragraph, QuillText, tokens } from '@treams/treams-ui';

import PopoverTarget from '../../popovers/PopoverTarget';
import GoalCreateCompetencyModal from './GoalCreateCompetencyModal';

const Container = styled(Box)<{ standalone?: boolean }>`
	background: ${tokens.surface.secondary};
	border: 1px solid ${(props) => props.theme.colors.lightBlueDark};
	color: ${tokens.text.secondary};

	${(props) =>
		props.standalone
			? `
		border-radius: 4px;
		border-radius: 4px;

		> :last-child {
			border-radius: 4px;
			border-radius: 4px;
		}
	`
			: `
		border-top: none;

		border-bottom-left-radius: 4px;
		border-bottom-right-radius: 4px;

		> :last-child {
			border-bottom-left-radius: 4px;
			border-bottom-right-radius: 4px;
		}
	`}
`;

const Square = styled(Box)<{ rotated?: boolean }>`
	width: 20px;
	height: 20px;
	background: ${(props) => props.theme.colors.lightBlueDark};
	border-radius: 4px;

	svg {
		transform: ${(props) => (props.rotated ? 'scale(0.8, 0.8) rotateZ(45deg)' : 'scale(0.8, 0.8)')};
	}

	${(props) =>
		props.rotated &&
		`
		&:hover {
			background: ${rgba(tokens.text.secondary, 0.5)};

			&:active {
				background: ${tokens.text.secondary};
			}
		}
	`}
`;

const InputContainer = styled(Flex)``;

const Row = styled(Box)<{ interactive?: boolean; border?: false; standalone?: boolean }>`
	padding: 8px ${(props) => (props.standalone ? 8 : 18)}px;

	${(props) =>
		props.border !== false && `border-top: 1px solid ${props.theme.colors.lightBlueDark};`}

	${(props) =>
		props.interactive &&
		`
		cursor: pointer;

		&:hover {
			background: ${rgba(tokens.border.subtle, 0.25)};

			&:active {
				background: ${rgba(tokens.border.subtle, 0.5)};
			}
		}`};
`;

const AddButton = styled(Row)<{ standalone?: boolean }>`
	align-items: center;
	padding: 8px ${(props) => (props.standalone ? 8 : 18)}px;
	border-top: none;
`;

const Input = styled.input`
	background: transparent;
	padding: 8px 18px;
	padding-left: 0px;
	flex-grow: 1;
	font-size: 14px;
	color: ${tokens.text.secondary};

	::placeholder {
		color: ${(props) => props.theme.colors.lightBlueDark};
	}
`;

const Label = styled(Box)`
	line-height: 20px;
`;

const TooltipTitle = styled.div`
	border-top: 1px solid ${tokens.border.subtle};
	padding-top: 10px;
	margin: 0px -20px;
	margin-top: 10px !important;
	margin-bottom: -5px;
	text-align: center;
`;

function GoalCreateCompetencySuggestion(props: {
	competency: graphql.Competency;
	onClick: () => void;
}) {
	const { localize } = useLocalize();

	return (
		<Row interactive onClick={props.onClick}>
			<Flex>
				<Box width={20} mr={2} flexGrow={0} flexShrink={0} />
				<Box>{localize(props.competency.title).text}</Box>
			</Flex>
		</Row>
	);
}

function GoalCreateCompetencyLevelOptionSelection(props: {
	option: graphql.CompetencyLevelOption;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	return (
		<Box flexShrink={0} flexGrow={0}>
			<PopoverTarget
				content={
					<Box style={{ padding: 20 }}>
						<QuillText
							value={localize(props.option.criteria ?? [])?.text}
							placeholder={t('evaluation.submission.competency.levels.noDescription')}
						/>
						<TooltipTitle>{localize(props.option.level.title)?.text}</TooltipTitle>
					</Box>
				}
			>
				<Flex alignItems="center">
					<Box flexGrow={0} flexShrink={0} mr={1} size={16}>
						<HammerIcon />
					</Box>
					<Box flexGrow={0} flexShrink={0}>
						{localize(props.option.level.title ?? []).text}
					</Box>
				</Flex>
			</PopoverTarget>
		</Box>
	);
}

export default function GoalCreateCompetency(props: { standalone?: boolean; required?: boolean }) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [adding, setAdding] = useState(false);
	const [input, setInput] = useState('');
	const [selectedCompetency, setSelectedCompetency] = useState<graphql.Competency | undefined>();
	const [{ value: competency }, , { setValue: setCompetency }] = useField<
		graphql.Competency | undefined
	>('competency');
	const [{ value: competencyLevelInput }, , { setValue: setCompetencyLevelInput }] = useField<
		{ competency: number } | undefined
	>('competencyLevelInput');

	const competencyLevelOption =
		competencyLevelInput && competency?.levels[competencyLevelInput.competency];

	const query = graphql.useCompetenciesWithLevelsQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const competencies = query.data?.competencies.edges.map(({ node }) => node as graphql.Competency);

	const matches =
		_.sortBy(
			competencies
				?.filter((competency) => competency.attachableToGoal)
				.filter((competency) =>
					localize(competency.title).text.toLowerCase().includes(input.toLowerCase()),
				) ?? [],
			(competency) => localize(competency.title).text.toLowerCase(),
		) ?? [];

	const reset = () => {
		setAdding(false);
		setInput('');
		setSelectedCompetency(undefined);
		setCompetency(undefined);
		setCompetencyLevelInput(undefined);
	};

	return (
		<Container standalone={props.standalone}>
			{competency && competencyLevelOption ? (
				<Row border={false} standalone={props.standalone}>
					<Flex alignItems="center">
						<Square
							mr={2}
							flexGrow={0}
							flexShrink={0}
							rotated
							style={{ cursor: 'pointer' }}
							onClick={reset}
						>
							<IconColor color="white">
								<AddIcon />
							</IconColor>
						</Square>
						<Box flexGrow={1} flexShrink={1}>
							{localize(competency.title).text}
						</Box>
						<GoalCreateCompetencyLevelOptionSelection option={competencyLevelOption} />
					</Flex>
				</Row>
			) : adding ? (
				<InputContainer>
					<Square
						mr={2}
						flexGrow={0}
						flexShrink={0}
						rotated
						style={{
							marginTop: 8,
							marginBottom: 8,
							marginLeft: props.standalone ? 8 : 18,
							cursor: 'pointer',
						}}
						onClick={reset}
					>
						<IconColor color="white">
							<AddIcon />
						</IconColor>
					</Square>
					<Input
						placeholder={t('goals.create.form.competency.startTyping')}
						autoFocus
						value={input}
						onChange={(event) => setInput(event.currentTarget.value)}
					/>
				</InputContainer>
			) : (
				<AddButton onClick={() => setAdding(true)} interactive standalone={props.standalone}>
					<Flex>
						<Square mr={2} flexGrow={0} flexShrink={0}>
							<IconColor color="white">
								<AddIcon />
							</IconColor>
						</Square>
						<Label>
							{t('goals.create.form.competency.placeholder')}
							{props.required ? null : (
								<>
									{' '}
									<Paragraph color="lightBlueDark">
										({t('goals.create.form.competency.placeholderOptional')})
									</Paragraph>
								</>
							)}
						</Label>
					</Flex>
				</AddButton>
			)}
			{adding && (
				<>
					{matches.map((match) => (
						<GoalCreateCompetencySuggestion
							key={match.id}
							competency={match as graphql.Competency}
							onClick={() => setSelectedCompetency(match as graphql.Competency)}
						/>
					))}
					{competencies && matches.length === 0 && input.length > 0 && (
						<Row>
							<Flex justifyContent="center">{t('goals.create.form.competency.noResults')}</Flex>
						</Row>
					)}
				</>
			)}
			{selectedCompetency && (
				<GoalCreateCompetencyModal
					competency={selectedCompetency}
					onCancel={reset}
					onSave={() => {
						setCompetency(selectedCompetency);
						setAdding(false);
						setSelectedCompetency(undefined);
					}}
				/>
			)}
		</Container>
	);
}
